import {
  supabase,
} from '@/lib/supabase';

export type CommunicationIdentitySource =
  | 'claimed-account'
  | 'family-graph-email';

export type CommunicationIdentityFailureCode =
  | 'INVALID_MEMBER_ID'
  | 'CLAIMED_ACCOUNT_UNRESOLVED'
  | 'UNCLAIMED_CONTACT_UNRESOLVED'
  | 'IDENTITY_LOOKUP_FAILED';

export type CommunicationIdentityInput =
  Readonly<{
    memberId: string;
    profileState?: string | null;
    claimedByUserId?: string | null;
    directEmail?: string | null;
  }>;

export type ResolvedCommunicationIdentity =
  Readonly<{
    ok: true;
    memberId: string;
    claimedUserId: string | null;
    email: string;
    source: CommunicationIdentitySource;
  }>;

export type FailedCommunicationIdentity =
  Readonly<{
    ok: false;
    memberId: string;
    code: CommunicationIdentityFailureCode;
    reason: string;
  }>;

export type CommunicationIdentityResult =
  | ResolvedCommunicationIdentity
  | FailedCommunicationIdentity;

type IdentityRpcRow =
  Readonly<{
    member_id?: string | null;
    claimed_user_id?: string | null;
    email?: string | null;
    profile_state?: string | null;
    resolution_source?: string | null;
  }>;

const normalizeEmail = (
  value: string | null | undefined,
): string =>
  (value ?? '')
    .trim()
    .toLowerCase();

const isValidEmail = (
  value: string,
): boolean =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(
    value,
  );

const failure = (
  memberId: string,
  code: CommunicationIdentityFailureCode,
  reason: string,
): FailedCommunicationIdentity =>
  Object.freeze({
    ok: false,
    memberId,
    code,
    reason,
  });

export async function resolveMemberCommunicationIdentity(
  input: CommunicationIdentityInput,
): Promise<CommunicationIdentityResult> {
  const memberId =
    input.memberId.trim();

  if (!memberId) {
    return failure(
      memberId,
      'INVALID_MEMBER_ID',
      'A family member identity is required.',
    );
  }

  const claimed =
    input.profileState === 'claimed' ||
    Boolean(
      input.claimedByUserId,
    );

  /*
   * Claimed profiles must always resolve through claimed_by_user_id.
   * A copied or stale family_graph_members.email value cannot override
   * the authenticated account that owns the claimed profile.
   */
  if (claimed) {
    try {
      const {
        data,
        error,
      } = await (
        supabase as any
      ).rpc(
        'resolve_claimed_member_communication_identity',
        {
          p_member_id: memberId,
        },
      );

      if (error) {
        return failure(
          memberId,
          'IDENTITY_LOOKUP_FAILED',
          error.message ||
            'The claimed account identity lookup failed.',
        );
      }

      const row:
        IdentityRpcRow | null =
          Array.isArray(data)
            ? data[0] ?? null
            : data ?? null;

      const email =
        normalizeEmail(
          row?.email,
        );

      if (
        !row ||
        !isValidEmail(email)
      ) {
        return failure(
          memberId,
          'CLAIMED_ACCOUNT_UNRESOLVED',
          'This claimed profile does not have a resolvable authenticated account.',
        );
      }

      return Object.freeze({
        ok: true,
        memberId:
          row.member_id ||
          memberId,
        claimedUserId:
          row.claimed_user_id ??
          input.claimedByUserId ??
          null,
        email,
        source:
          'claimed-account',
      });
    } catch (error) {
      return failure(
        memberId,
        'IDENTITY_LOOKUP_FAILED',
        error instanceof Error
          ? error.message
          : String(error),
      );
    }
  }

  /*
   * Unclaimed or invited contacts may use their explicit family-row
   * contact email. This fallback is never authoritative for a claimed
   * profile.
   */
  const directEmail =
    normalizeEmail(
      input.directEmail,
    );

  if (
    isValidEmail(
      directEmail,
    )
  ) {
    return Object.freeze({
      ok: true,
      memberId,
      claimedUserId: null,
      email: directEmail,
      source:
        'family-graph-email',
    });
  }

  return failure(
    memberId,
    'UNCLAIMED_CONTACT_UNRESOLVED',
    'This family profile is not linked to a communication account.',
  );
}
